import React, { useEffect, useState } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { SectionHeader } from '../components/UI';
import { api, moneyFull, pct } from '../lib/api';
import { Check, AlertTriangle, Minus } from 'lucide-react';

const QUARTER_TONES = {
  green: { bar: 'bg-indie-evergreen', text: 'text-indie-evergreen', trackFill: 'bg-indie-evergreen/15' },
  warn: { bar: 'bg-indie-cobalt', text: 'text-indie-cobalt', trackFill: 'bg-indie-cobalt/15' },
  red: { bar: 'bg-indie-neg-dark', text: 'text-indie-neg-dark', trackFill: 'bg-indie-neg-dark/15' },
};

function QuarterBar({ pctVal, label, target, tone, quarterState, breakdown }) {
  const palette = QUARTER_TONES[tone];
  const pctClamped = Math.min(Math.max(pctVal, 0), 1.2);
  const scale = 1.2; // bar track covers 0-120%
  // For closed quarters: single actual block. For in-progress: actual + projected.
  const actualPct = breakdown.kind === 'projected'
    ? Math.min((breakdown.actual / target) || 0, scale)
    : pctClamped;
  const projectedPct = breakdown.kind === 'projected'
    ? Math.min(pctClamped, scale)
    : null;
  const overshoot = Math.max(0, pctClamped - 1);
  return (
    <div className={`bg-white border-t-4 ${tone === 'green' ? 'border-indie-evergreen' : tone === 'warn' ? 'border-indie-cobalt' : 'border-indie-neg-dark'} p-6`}>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="label-editorial">{label}</div>
          <div className="text-[11px] text-indie-ink/50 uppercase tracking-wider mt-0.5">{quarterState}</div>
        </div>
        <div className="text-right">
          <div className={`text-lg tracking-tight font-semibold num-tabular ${palette.text}`}>{pct(pctVal)}</div>
          {breakdown.kind === 'projected' && <div className="text-[10px] text-indie-ink/50 uppercase tracking-wider">projected</div>}
        </div>
      </div>
      <div className="relative mt-4">
        <div className="h-4 bg-indie-navy/5 border border-indie-navy/10 relative overflow-hidden">
          {/* Actual portion (solid) */}
          <div className={`absolute left-0 top-0 h-full ${palette.bar} transition-all`} style={{ width: `${(actualPct / scale) * 100}%` }} />
          {/* Projected portion (hatched/lighter) for in-progress quarter */}
          {projectedPct !== null && projectedPct > actualPct && (
            <div
              className={`absolute top-0 h-full ${palette.bar} opacity-35 border-l border-white/60`}
              style={{ left: `${(actualPct / scale) * 100}%`, width: `${((projectedPct - actualPct) / scale) * 100}%` }}
              title="Projected remainder of the quarter"
            />
          )}
          {/* Overshoot past 100% for already-closed quarters (lighter shade) */}
          {breakdown.kind !== 'projected' && overshoot > 0 && (
            <div
              className={`absolute top-0 h-full ${palette.bar} opacity-60 border-l border-white/40`}
              style={{ left: `${(1 / scale) * 100}%`, width: `${(overshoot / scale) * 100}%` }}
              title={`Over target by ${pct(overshoot)}`}
            />
          )}
        </div>
        {/* Target marker at 100% */}
        <div className="absolute top-0 h-4 w-[2px] bg-indie-navy" style={{ left: `${(1 / scale) * 100}%` }} />
        <div className="absolute -top-1 text-[10px] uppercase tracking-wider text-indie-navy font-semibold" style={{ left: `${(1 / scale) * 100}%`, transform: 'translateX(-50%) translateY(-100%)' }}>Target</div>
      </div>
      {breakdown.kind === 'projected' ? (
        <div className="mt-5 space-y-2 text-sm num-tabular">
          <div className="flex justify-between">
            <span className="text-indie-ink/60">Actual so far <span className="text-indie-ink/40 text-xs">({breakdown.actualNote})</span></span>
            <span className="font-semibold text-indie-evergreen">{moneyFull(breakdown.actual)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-indie-ink/60">Projected remainder <span className="text-indie-ink/40 text-xs">({breakdown.remainderNote})</span></span>
            <span className="text-indie-ink/70">{moneyFull(breakdown.remainder)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-indie-navy/10">
            <span className="font-semibold text-indie-navy">Projected quarter total</span>
            <span className="font-semibold text-indie-navy">{moneyFull(breakdown.total)} <span className="text-indie-ink/50 font-normal">of {moneyFull(target)}</span></span>
          </div>
        </div>
      ) : (
        <div className="mt-5 flex justify-between text-sm num-tabular">
          <div><span className="text-indie-ink/60">Actual</span> <span className="font-semibold text-indie-navy">{moneyFull(breakdown.actual)}</span></div>
          <div className="text-right"><span className="text-indie-ink/60">Target</span> <span className="font-semibold text-indie-navy">{moneyFull(target)}</span></div>
        </div>
      )}
    </div>
  );
}

function MetricCompare({ label, target, actual, compareFn, formatter }) {
  // compareFn returns a number: positive = ahead of target, negative = behind
  const hasActual = actual !== null && actual !== undefined && !Number.isNaN(actual);
  const delta = hasActual ? compareFn(actual, target) : null;
  const status = !hasActual ? 'pending' : delta >= 0 ? 'green' : delta >= -0.05 ? 'warn' : 'red';
  const Icon = status === 'green' ? Check : status === 'pending' ? Minus : AlertTriangle;
  const colorClass = status === 'green' ? 'text-indie-evergreen' : status === 'warn' ? 'text-indie-cobalt' : status === 'red' ? 'text-indie-neg-dark' : 'text-indie-ink/40';
  const borderClass = status === 'green' ? 'border-indie-evergreen' : status === 'warn' ? 'border-indie-cobalt' : status === 'red' ? 'border-indie-neg-dark' : 'border-indie-navy/30';
  return (
    <div className={`bg-white border-t-4 ${borderClass} p-5`}>
      <div className="flex items-start justify-between gap-2">
        <div className="label-editorial">{label}</div>
        <Icon className={colorClass} size={16} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] text-indie-ink/50 uppercase tracking-wider">Target</div>
          <div className="mt-0.5 text-xl font-semibold text-indie-navy num-tabular">{formatter(target)}</div>
        </div>
        <div>
          <div className="text-[11px] text-indie-ink/50 uppercase tracking-wider">Actual</div>
          <div className={`mt-0.5 text-xl font-semibold num-tabular ${hasActual ? colorClass : 'text-indie-ink/40'}`}>
            {hasActual ? formatter(actual) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BHAG() {
  const { forecastData } = useScenario();
  const [bhag, setBhag] = useState(null);
  const [actuals, setActuals] = useState([]);
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    api.get('/bhag').then((r) => setBhag(r.data));
    api.get('/actuals').then((r) => setActuals(r.data));
    api.get('/daily-pulse/today').then((r) => setPulse(r.data)).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!bhag || !forecastData) return <div className="label-editorial">Loading…</div>;

  const totalActual = actuals.reduce((a, b) => a + b.revenue, 0);
  const forecastTotal = forecastData.forecast.totals.revenue;
  const fullYear = totalActual + forecastTotal;
  const annualPct = fullYear / bhag.annual_revenue;

  const tone = (p) => (p >= 0.95 ? 'green' : p >= 0.8 ? 'warn' : 'red');

  // Quarterly bucketing — know which months are closed (actuals) vs forecast.
  // We parse each month label ("Jan 2026", "Apr 2026", ...) into a calendar
  // year + quarter so the BHAG Q1/Q2/Q3/Q4 buckets are populated correctly
  // regardless of where the forecast window starts.
  const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const parseLabel = (label) => {
    // "Apr 2026" → {year: 2026, quarter: 1}
    const parts = (label || '').trim().split(/\s+/);
    if (parts.length < 2) return null;
    const m = parts[0].slice(0, 3).toLowerCase();
    const year = parseInt(parts[1], 10);
    const monthIdx = MONTH_NAMES.indexOf(m);
    if (monthIdx < 0 || Number.isNaN(year)) return null;
    return { year, quarter: Math.floor(monthIdx / 3) };
  };
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentQuarterIdx = Math.floor(today.getMonth() / 3);
  // BHAG quarters track calendar quarters of the current fiscal year.
  const targetYear = currentYear;

  const quarterBuckets = [
    { actualsSum: 0, forecastSum: 0, actualMonths: 0, forecastMonths: 0 },
    { actualsSum: 0, forecastSum: 0, actualMonths: 0, forecastMonths: 0 },
    { actualsSum: 0, forecastSum: 0, actualMonths: 0, forecastMonths: 0 },
    { actualsSum: 0, forecastSum: 0, actualMonths: 0, forecastMonths: 0 },
  ];
  actuals.forEach((a) => {
    const parsed = parseLabel(a.month_label);
    if (!parsed || parsed.year !== targetYear) return;
    quarterBuckets[parsed.quarter].actualsSum += a.revenue;
    quarterBuckets[parsed.quarter].actualMonths += 1;
  });
  forecastData.forecast.months.forEach((m) => {
    const parsed = parseLabel(m.label);
    if (!parsed || parsed.year !== targetYear) return;
    const inActuals = actuals.some((a) => a.month_label === m.label);
    if (inActuals) return;
    quarterBuckets[parsed.quarter].forecastSum += m.income.total;
    quarterBuckets[parsed.quarter].forecastMonths += 1;
  });

  const quarters = bhag.quarters.map((q, i) => {
    const b = quarterBuckets[i];
    const hasActuals = b.actualMonths > 0;
    const hasForecast = b.forecastMonths > 0;

    if (i === currentQuarterIdx) {
      // "In progress" — split actual vs projected explicitly.
      const currentMonthLabel = forecastData.forecast.months[0]?.label;
      const currentMonthForecast = forecastData.forecast.months[0]?.income?.total || 0;
      const mtd = pulse?.today?.monthly_revenue_to_date || 0;
      const actualSoFar = b.actualsSum + mtd;
      // Rest-of-current-month estimate: forecast for that month minus MTD, floor at 0.
      const restOfCurrent = Math.max(0, currentMonthForecast - mtd);
      // Future months in this quarter from forecast (not the current month, which we just covered).
      const futureMonthsForecast = Math.max(0, b.forecastSum - currentMonthForecast);
      const projectedRemainder = restOfCurrent + futureMonthsForecast;
      const projectedTotal = actualSoFar + projectedRemainder;
      return {
        q,
        pctVal: q.revenue_target ? projectedTotal / q.revenue_target : 0,
        quarterState: 'In progress',
        breakdown: {
          kind: 'projected',
          actual: actualSoFar,
          actualNote: `through ${currentMonthLabel} (MTD)`,
          remainder: projectedRemainder,
          remainderNote: 'rest of quarter forecast',
          total: projectedTotal,
        },
      };
    }

    // Past quarter — actuals are all we care about.
    const totalQ = b.actualsSum + b.forecastSum;
    const quarterState = hasActuals && hasForecast ? 'Partially closed' : 'Closed';
    return {
      q,
      pctVal: q.revenue_target ? totalQ / q.revenue_target : 0,
      quarterState,
      breakdown: { kind: 'actual', actual: totalQ },
    };
  });

  // Hide future quarters on this page — their "numbers" are pure scenario projections
  // and muddy the "where we are right now" signal. Dashboard already covers forecast.
  const visibleQuarters = quarters.filter((_, i) => i <= currentQuarterIdx);

  // Actuals from today's daily pulse post (for the bottom target-vs-actual cards).
  const t = pulse?.today || null;
  const avgPriceActual = t && t.sales_count ? t.monthly_revenue_to_date / t.sales_count : null;
  const conversionActual = t?.conversion_rate ?? null;
  const cplActual = t && t.calls_booked ? t.ad_spend / t.calls_booked : null;
  const adSpendPctActual = t && t.monthly_revenue_to_date ? t.ad_spend / t.monthly_revenue_to_date : null;
  const roasActual = t?.roas ?? null;
  // YTD net margin from closed actuals only
  const closedRev = actuals.reduce((a, b) => a + b.revenue, 0);
  const closedNet = actuals.reduce((a, b) => a + b.net_income, 0);
  const netMarginActual = closedRev ? closedNet / closedRev : null;

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="BHAG · Skybreaker"
        title="$3.355M @ 20% net margin"
        description="Our stretch target for the year. Pacing compares actuals-plus-forecast to the target, quarter by quarter."
      />

      <div className="bg-indie-navy text-indie-cream p-8">
        <div className="label-editorial-cream">Full year</div>
        <div className="mt-2 text-5xl font-semibold text-white num-tabular">{pct(annualPct)}</div>
        <div className="mt-2 text-indie-cream/70 num-tabular">
          {moneyFull(fullYear)} of {moneyFull(bhag.annual_revenue)}
        </div>
        <div className="mt-6 h-3 bg-indie-ink relative overflow-hidden">
          <div className="absolute left-0 top-0 h-full bg-indie-yellow transition-all" style={{ width: `${Math.min(annualPct, 1.15) * 100}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {visibleQuarters.map(({ q, pctVal, quarterState, breakdown }) => (
          <QuarterBar
            key={q.label}
            label={q.label}
            target={q.revenue_target}
            pctVal={pctVal}
            tone={tone(pctVal)}
            quarterState={quarterState}
            breakdown={breakdown}
          />
        ))}
      </div>
      {currentQuarterIdx < 3 && (
        <div className="-mt-4 text-[11px] text-indie-ink/50 leading-relaxed">
          Future quarters (Q{currentQuarterIdx + 2}{currentQuarterIdx + 2 < 4 ? `–Q4` : ''} {targetYear}) are hidden on this page — their numbers are forecast-only and change every time you edit a driver. To see the full-year forecast and whether your current scenario gets you to the BHAG, use the Dashboard.
        </div>
      )}

      <div>
        <div className="label-editorial mb-3">Operating metrics · target vs. actual</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <MetricCompare
            label="Average price point"
            target={bhag.average_price_point}
            actual={avgPriceActual}
            compareFn={(a, t) => (a - t) / t}
            formatter={moneyFull}
          />
          <MetricCompare
            label="Qualified-call conversion"
            target={bhag.conversion_rate}
            actual={conversionActual}
            compareFn={(a, t) => a - t}
            formatter={(v) => pct(v)}
          />
          <MetricCompare
            label="Cost per lead"
            target={bhag.target_cpl}
            actual={cplActual}
            compareFn={(a, t) => (t - a) / t}
            formatter={moneyFull}
          />
          <MetricCompare
            label="Ad spend cap (% of revenue)"
            target={bhag.ad_spend_cap_pct}
            actual={adSpendPctActual}
            compareFn={(a, t) => t - a}
            formatter={(v) => pct(v)}
          />
          <MetricCompare
            label="Google ROAS"
            target={bhag.target_roas}
            actual={roasActual}
            compareFn={(a, t) => a - t}
            formatter={(v) => `${Number(v).toFixed(1)}x`}
          />
          <MetricCompare
            label="Net margin (YTD closed months)"
            target={bhag.annual_net_margin}
            actual={netMarginActual}
            compareFn={(a, t) => a - t}
            formatter={(v) => pct(v)}
          />
        </div>
        <div className="mt-3 text-[11px] text-indie-ink/50 leading-relaxed">
          Actuals for price point, conversion, CPL, ad-spend ratio, and ROAS come from today's <span className="font-mono">#dailyhighlight</span> post. Net margin is computed from closed-month QuickBooks actuals only.
        </div>
      </div>

      <div className="text-xs text-indie-ink/50">Last synced {new Date(bhag.last_synced).toLocaleString()} · source: BHAG Numbers · Indie Law × Skybreaker</div>
    </div>
  );
}
